/**
 * Scleral lens power from the trial lens on the eye and an over-refraction.
 *
 * 1. The over-refraction, in minus cylinder, as its two principal meridians:
 *    the sphere along the axis, sphere + cylinder at 90° to it.
 * 2. Each meridian moved from the spectacle plane to the eye, F / (1 − dF),
 *    when it is over 4 D.
 * 3. The trial lens added to both meridians (the trial lenses are spheres).
 * 4. The base curve change from the trial lens to the lens ordered,
 *    337.5 × (1/r trial − 1/r ordered), added to both: a flatter base curve
 *    makes the tear lens less plus, so the lens needs more plus.
 * 5. Each meridian rounded to 0.25 D, an exact half toward the more plus
 *    power, and written back as sphere, cylinder and axis.
 */

export const VERTEX_ABOVE_D = 4;
export const TEAR_LENS_FACTOR = 337.5;

/** A meridian's power at the eye, from the spectacle plane d mm in front of it. */
export const vertex = (power, mm) => (Math.abs(power) > VERTEX_ABOVE_D ? power / (1 - (mm / 1000) * power) : power);

/** To the nearest 0.25 D, an exact half to the more plus power. */
export const roundQuarter = (v) => Math.floor(v * 4 + 0.5 + 1e-9) / 4 + 0;

/** The power the lens needs to make up for a change of base curve, in D. */
export const baseCurveChange = (fromMm, toMm) => TEAR_LENS_FACTOR * (1 / fromMm - 1 / toMm);

/** The axis 90° away, 1 to 180. */
export const across = (axis) => ((axis + 90 - 1) % 180) + 1;

const NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)$/;

/** A power typed in: blank is null, "PL" is 0; throws if it does not read as one. */
export function readPower(text, name, limit) {
  const s = String(text ?? "").trim().toLowerCase().replace(/[−–]/g, "-");
  if (!s) return null;
  if (s === "pl" || s === "plano") return 0;
  if (!NUMBER.test(s)) throw new Error(`The ${name} should be a number of dioptres, like −1.25.`);
  const v = parseFloat(s);
  if (Math.abs(v) > limit) throw new Error(`The ${name} should be within ±${limit} D.`);
  return v;
}

/** An axis typed in: 1 to 180 (0 is read as 180). */
export function readAxis(text) {
  const s = String(text ?? "").trim();
  if (!s) return null;
  if (!/^\d{1,3}$/.test(s) || Number(s) > 180) throw new Error("The axis should be a whole number from 1 to 180.");
  return Number(s) % 180 || 180;
}

/** The over-refraction in minus cylinder. */
export function minusCyl({ sphere, cyl, axis }) {
  if (!cyl) return { sphere, cyl: 0, axis: null };
  if (cyl < 0) return { sphere, cyl, axis };
  return { sphere: sphere + cyl, cyl: -cyl, axis: across(axis) };
}

/** "−2.00", "+1.25", "PL". */
export function dioptres(p) {
  if (Math.abs(p) < 0.005) return "PL";
  return `${p > 0 ? "+" : "−"}${Math.abs(p).toFixed(2)}`;
}

/** "−2.25 −0.75 × 90", "−2.25 DS", "PL". */
export function formatRx({ sphere, cyl, axis }) {
  if (!cyl) return Math.abs(sphere) < 0.005 ? "PL" : `${dioptres(sphere)} DS`;
  return `${dioptres(sphere)} ${dioptres(cyl)} × ${axis}`;
}

/** A change in D, always signed: "+0.62", "−1.14", "0.00". */
export const change = (p) => (Math.abs(p) < 0.005 ? "0.00" : `${p > 0 ? "+" : "−"}${Math.abs(p).toFixed(2)}`);

/**
 * The lens power, meridian by meridian.
 *   overRefraction  { sphere, cyl, axis }, as written (either cylinder sign)
 *   vertexMm        the over-refraction's vertex distance
 *   trial           { code, power, bc } -- the lens on the eye
 *   orderedBc       the base curve of the lens to order, mm (null: no change)
 * Returns the meridians, the rows that build them, and the lens written out.
 */
export function scleralPower({ overRefraction, vertexMm = 12, trial, orderedBc = null }) {
  const or = minusCyl(overRefraction);
  const axes = or.cyl ? [or.axis, across(or.axis)] : [null];
  const at = (f) => axes.map((_, i) => f(i === 0 ? or.sphere : or.sphere + or.cyl));

  const rows = [];
  let powers = at((p) => p);
  rows.push({ label: "Over-refraction", values: powers, kind: "given" });

  const atEye = powers.map((p) => vertex(p, vertexMm));
  if (atEye.some((p, i) => p !== powers[i])) {
    rows.push({ label: `At the eye (${vertexMm} mm)`, values: atEye, kind: "step" });
  }
  powers = atEye;

  powers = powers.map((p) => p + trial.power);
  rows.push({ label: `Trial lens ${trial.code}`, values: axes.map(() => trial.power), kind: "add" });

  const bc = typeof orderedBc === "number" && typeof trial.bc === "number" && orderedBc !== trial.bc
    ? baseCurveChange(trial.bc, orderedBc) : 0;
  if (bc) {
    powers = powers.map((p) => p + bc);
    rows.push({ label: `Base curve ${trial.bc.toFixed(2)} → ${orderedBc.toFixed(2)}`, values: axes.map(() => bc), kind: "add" });
  }

  rows.push({ label: "Total", values: powers, kind: "total" });
  const rounded = powers.map(roundQuarter);

  const sphere = rounded[0];
  const cyl = rounded.length > 1 ? rounded[1] - rounded[0] : 0;
  const result = { sphere, cyl, axis: cyl ? or.axis : null };
  return { axes, rows, unrounded: powers, rounded, baseCurveChange: bc, result, text: formatRx(result) };
}
